import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { WizardData, CreatedDocument, DocCategory, DOC_CATEGORIES, ReceiptWithAllocationsResult, PaymentLine, InvoiceAllocation } from './types';
import { createInvoice, createReceipt, getVatRate } from '../../api/client';
import { calculateReviewTotals } from './reviewTotals';

interface ReviewStepProps {
  data: WizardData;
  onCommentsChange: (comments: string) => void;
  onAllocationsChange?: (allocations: InvoiceAllocation[]) => void;
  onSuccess: (doc: CreatedDocument, allocationsResult?: ReceiptWithAllocationsResult) => void;
}

const CATEGORY_BADGE_STYLES: Record<DocCategory, string> = {
  invoice: 'bg-blue-100 text-blue-700 border-blue-300',
  receipt: 'bg-green-100 text-green-700 border-green-300',
  delivery_note: 'bg-orange-100 text-orange-700 border-orange-300',
  invoice_receipt: 'bg-purple-100 text-purple-700 border-purple-300',
};

export default function ReviewStep({ data, onCommentsChange, onAllocationsChange, onSuccess }: ReviewStepProps) {
  const { t } = useTranslation();
  const [vatRate, setVatRate] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const createInFlightRef = useRef(false);

  const mapPayment = (p: PaymentLine) => {
    const pay: Record<string, unknown> = {
      payment_type: p.payment_type,
      amount_nis: p.amount,
    };
    if (p.check_number) pay.check_number = p.check_number;
    if (p.bank_code) pay.bank_code = p.bank_code;
    if (p.branch_number) pay.branch_number = p.branch_number;
    if (p.bank_account_number) pay.bank_account_number = p.bank_account_number;
    if (p.due_date) pay.due_date = p.due_date;
    return pay;
  };
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getVatRate()
      .then((res) => {
        if (!cancelled) {
          const rate = res.data?.vat_rate ?? res.data?.rate ?? res.data;
          setVatRate(typeof rate === 'number' ? rate : null);
        }
      })
      .catch(() => {
        // VAT rate is non-critical; continue without it
      });
    return () => { cancelled = true; };
  }, []);

  // Auto-distribute payment total across invoices on mount
  useEffect(() => {
    if (data.category !== 'receipt' || !onAllocationsChange || data.invoiceAllocations.length === 0) return;
    const paymentTotal = data.payments.reduce((s, p) => s + p.amount, 0);
    const allocationTotal = data.invoiceAllocations.reduce((s, a) => s + a.amount_close, 0);
    if (paymentTotal >= allocationTotal - 0.01) return; // allocations already fit within payment

    let remaining = paymentTotal;
    const distributed = data.invoiceAllocations.map((a) => {
      const cap = a.open_balance ?? a.amount_close;
      const closeAmount = Math.min(cap, Math.max(0, remaining));
      remaining = Math.max(0, remaining - closeAmount);
      return { ...a, amount_close: Math.round(closeAmount * 100) / 100 };
    });
    onAllocationsChange(distributed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoryConfig = DOC_CATEGORIES.find((c) => c.id === data.category);
  const categoryLabel = categoryConfig ? t(categoryConfig.titleKey) : '';

  const { subtotal, vatAmount, total, showVat } = calculateReviewTotals({
    category: data.category,
    itemsSubtotal: data.items.reduce((sum, item) => sum + item.quantity * item.price, 0),
    paymentsTotal: data.payments.reduce((sum, p) => sum + p.amount, 0),
    vatRate,
    priceIncludeVat: data.priceIncludeVat,
  });

  // Check if invoice allocation total matches the payment total
  const allocationMismatch = data.category === 'receipt' && data.invoiceAllocations.length > 0 && (() => {
    const paymentTotal = data.payments.reduce((s, p) => s + p.amount, 0);
    const allocationTotal = data.invoiceAllocations.reduce((s, a) => s + a.amount_close, 0);
    return Math.abs(paymentTotal - allocationTotal) > 0.01;
  })();

  const handleCreate = useCallback(async () => {
    if (createInFlightRef.current) return;
    createInFlightRef.current = true;
    setLoading(true);
    setError(null);

    try {
      let res;

      if (data.category === 'receipt') {
        const payload: Record<string, unknown> = {
          receipt_type: data.receiptType,
          customer_id: data.customer?.customer_id || 0,
          payments: data.payments.map(mapPayment),
          comments: data.comments || undefined,
          send_mail: false,
        };
        if (data.invoiceAllocations.length > 0) {
          const activeAllocations = data.invoiceAllocations.filter((a) => a.amount_close > 0);
          if (activeAllocations.length > 0) {
            payload.invoices_to_close = activeAllocations.map((a) => ({
              document_type: a.document_type,
              document_number: a.document_number,
              amount_close: a.amount_close,
              invoice_total: a.invoice_total,
              open_balance: a.open_balance,
              document_date: a.document_date,
            }));
          }
        }
        res = await createReceipt(payload);
        // If response contains allocation results, pass them to success handler
        const resData = res.data;
        if (resData?.invoice_allocations || resData?.failed_invoice_allocations) {
          onSuccess(resData.receipt as CreatedDocument, resData as ReceiptWithAllocationsResult);
        } else {
          onSuccess(resData as CreatedDocument);
        }
      } else {
        const payload: Record<string, unknown> = {
          document_type: data.docType,
          customer_id: data.customer?.customer_id || 0,
          items: data.items.map((item) => ({
            item_id: item.item_id || undefined,
            catalog_number: item.catalog_number || undefined,
            quantity: item.quantity,
            description: item.description,
            price_nis: item.price,
          })),
          comments: data.comments || undefined,
          price_include_vat: false,
          send_mail: false,
        };
        if (data.category === 'invoice_receipt' && data.payments.length > 0) {
          payload.payments = data.payments.map(mapPayment);
        }
        if (data.category === 'invoice' && data.invoiceSourceMode === 'delivery_notes' && data.deliveryNotes.length > 0) {
          payload.delivery_notes_to_close = data.deliveryNotes.map((note) => ({
            document_type: note.document_type,
            document_number: note.document_number,
            amount_close: note.amount,
            document_total: note.amount,
            document_date: note.document_date,
          }));
        }
        res = await createInvoice(payload);
        onSuccess(res.data as CreatedDocument);
      }
    } catch (err: unknown) {
      const status = (err as any)?.response?.status;
      const serverDetail = (err as any)?.response?.data?.detail;
      let message: string;
      if (status === 409) {
        // Differentiate receipt-allocation conflicts, stale receipt-close
        // conflicts, and document-numbering conflicts.
        const isAllocationConflict = typeof serverDetail === 'string' && /already.*allocated|fully.*closed/i.test(serverDetail);
        if (isAllocationConflict) {
          message = t('createDocument.review.allocationConflict', 'The selected invoice is already fully allocated. Please go back and adjust the receipt amounts.');
        } else if (data.category === 'receipt') {
          // A 409 while closing a receipt means the linked invoice changed
          // underneath us (already closed or updated by another process).
          message = t('createDocument.review.receiptConflict', 'The linked invoice was already closed or updated elsewhere. Please go back and refresh the invoice before retrying.');
        } else {
          message = t('createDocument.review.conflictError', 'A document with this number already exists. Please try again to generate a new document number.');
        }
      } else if (status && status >= 500) {
        message = t('createDocument.review.serverError', 'The accounting service encountered an error. Please try again in a moment.');
      } else if (status === 429 || status === 503) {
        message = t('createDocument.review.serviceUnavailable', 'The accounting service is temporarily unavailable. Please try again shortly.');
      } else {
        message = t('createDocument.review.error', 'Something went wrong while creating the document. Please try again.');
      }
      setError(message);
    } finally {
      createInFlightRef.current = false;
      setLoading(false);
    }
  }, [data, onSuccess, t]);

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 space-y-6">
        {/* Document Type Badge */}
        {data.category && (
          <div className="flex items-center gap-3">
            <span className="text-2xl">{categoryConfig?.icon}</span>
            <span
              className={`inline-block rounded-full border px-4 py-1 text-sm font-semibold ${CATEGORY_BADGE_STYLES[data.category]}`}
            >
              {categoryLabel}
            </span>
          </div>
        )}

        {/* Customer */}
        {data.customer && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
              {t('createDocument.review.customer')}
            </p>
            <p className="mt-1 text-lg font-semibold text-gray-900">
              {data.customer.display_name}
            </p>
          </div>
        )}

        {/* Items Table (invoice / delivery_note) */}
        {data.category !== 'receipt' && data.items.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
              {t('createDocument.review.items')}
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500">
                    <th className="px-4 py-2 text-start font-medium">
                      {t('createDocument.items.description')}
                    </th>
                    <th className="px-4 py-2 text-center font-medium">
                      {t('createDocument.items.qty')}
                    </th>
                    <th className="px-4 py-2 text-end font-medium">
                      {t('createDocument.items.price')}
                    </th>
                    <th className="px-4 py-2 text-end font-medium">
                      {t('createDocument.items.lineTotal')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item, idx) => (
                    <tr
                      key={item.id}
                      className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                    >
                      <td className="px-4 py-2 text-gray-800">{item.description}</td>
                      <td className="px-4 py-2 text-center text-gray-600">
                        {item.quantity}
                      </td>
                      <td className="px-4 py-2 text-end text-gray-600">
                        ₪{Number(item.price).toFixed(2)}
                      </td>
                      <td className="px-4 py-2 text-end font-medium text-gray-800">
                        ₪{Number(item.quantity * item.price).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.category === 'invoice' && data.invoiceSourceMode === 'delivery_notes' && data.deliveryNotes.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
              {t('createDocument.deliveryNotes.reviewTitle')}
            </p>
            <div className="overflow-x-auto rounded-xl border border-orange-100 bg-orange-50/40">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-orange-700">
                    <th className="px-4 py-2 text-start font-medium">{t('createDocument.deliveryNotes.number')}</th>
                    <th className="px-4 py-2 text-start font-medium">{t('createDocument.invoices.date')}</th>
                    <th className="px-4 py-2 text-end font-medium">{t('createDocument.deliveryNotes.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.deliveryNotes.map((note) => (
                    <tr key={`${note.document_type}-${note.document_number}`} className="border-t border-orange-100 bg-white/60">
                      <td className="px-4 py-2 font-medium text-gray-800">{note.document_number}</td>
                      <td className="px-4 py-2 text-gray-600">{note.document_date || '—'}</td>
                      <td className="px-4 py-2 text-end text-gray-700">₪{Number(note.amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-orange-700">
              {t('createDocument.deliveryNotes.closeNotice')}
            </p>
          </div>
        )}

        {/* Payments Table (receipt or invoice_receipt) */}
        {(data.category === 'receipt' || data.category === 'invoice_receipt') && data.payments.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
              {t('createDocument.review.payments')}
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500">
                    <th className="px-4 py-2 text-start font-medium">
                      {t('createDocument.payments.title')}
                    </th>
                    <th className="px-4 py-2 text-end font-medium">
                      {t('createDocument.payments.amount')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.map((p, idx) => (
                    <tr
                      key={idx}
                      className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                    >
                      <td className="px-4 py-2 text-gray-800">{p.payment_name}</td>
                      <td className="px-4 py-2 text-end text-gray-600">
                        ₪{Number(p.amount).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Invoice Allocations – editable on review */}
        {data.category === 'receipt' && data.invoiceAllocations.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
              {t('createDocument.success.invoicesClosed')}
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500">
                    {onAllocationsChange && (
                      <th className="px-3 py-2 w-10"></th>
                    )}
                    <th className="px-4 py-2 text-start font-medium">
                      {t('createDocument.invoices.invoiceNumber')}
                    </th>
                    <th className="px-4 py-2 text-end font-medium">
                      {t('createDocument.invoices.openBalance')}
                    </th>
                    <th className="px-4 py-2 text-end font-medium">
                      {t('createDocument.invoices.amountToClose')}
                    </th>
                    <th className="px-4 py-2 text-start font-medium">
                      {t('createDocument.invoices.status')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.invoiceAllocations.map((alloc, idx) => {
                    const isFullClose = alloc.open_balance != null &&
                      Math.abs(alloc.amount_close - alloc.open_balance) < 0.01;
                    const isExcluded = alloc.amount_close === 0;
                    return (
                      <tr
                        key={`${alloc.document_type}-${alloc.document_number}`}
                        className={`transition-colors ${isExcluded ? 'opacity-50 bg-gray-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                      >
                        {onAllocationsChange && (
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={!isExcluded}
                              onChange={() => {
                                const updated = data.invoiceAllocations.map((a) =>
                                  a.document_type === alloc.document_type && a.document_number === alloc.document_number
                                    ? { ...a, amount_close: isExcluded ? (a.open_balance ?? 0) : 0 }
                                    : a,
                                );
                                onAllocationsChange(updated);
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                          </td>
                        )}
                        <td className="px-4 py-2 text-gray-800 font-medium">
                          {alloc.document_number}
                        </td>
                        <td className="px-4 py-2 text-end text-gray-600">
                          {alloc.open_balance != null ? `₪${alloc.open_balance.toFixed(2)}` : '—'}
                        </td>
                        <td className="px-4 py-2 text-end">
                          {onAllocationsChange && !isExcluded ? (
                            <input
                              type="number"
                              min={0.01}
                              max={alloc.open_balance ?? undefined}
                              step={0.01}
                              value={alloc.amount_close || ''}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                const updated = data.invoiceAllocations.map((a) =>
                                  a.document_type === alloc.document_type && a.document_number === alloc.document_number
                                    ? { ...a, amount_close: val }
                                    : a,
                                );
                                onAllocationsChange(updated);
                              }}
                              className={`w-28 rounded-lg border p-1.5 text-end text-sm font-medium
                                focus:outline-none focus:ring-2 transition-all
                                ${alloc.open_balance && alloc.amount_close > alloc.open_balance + 0.01
                                  ? 'border-red-300 focus:ring-red-200 text-red-700'
                                  : 'border-gray-300 focus:ring-blue-200 text-gray-800'
                                }`}
                            />
                          ) : (
                            <span className="font-medium text-gray-800">
                              ₪{alloc.amount_close.toFixed(2)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`text-xs font-semibold ${
                            isExcluded ? 'text-gray-400' : isFullClose ? 'text-green-600' : 'text-orange-600'
                          }`}>
                            {isExcluded
                              ? t('createDocument.invoices.notSelected')
                              : isFullClose
                                ? t('createDocument.invoices.fullyClosed')
                                : t('createDocument.invoices.partiallyClosed')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-2 flex justify-between text-sm text-gray-600 px-1">
              <span>{t('createDocument.invoices.allocatedAmount')}: ₪{data.invoiceAllocations.reduce((s, a) => s + a.amount_close, 0).toFixed(2)}</span>
              {(() => {
                const paymentTotal = data.payments.reduce((s, p) => s + p.amount, 0);
                const allocationTotal = data.invoiceAllocations.reduce((s, a) => s + a.amount_close, 0);
                const diff = paymentTotal - allocationTotal;
                if (Math.abs(diff) < 0.01) {
                  return <span className="text-green-600 font-medium">{t('createDocument.invoices.fullyAllocated')}</span>;
                }
                if (diff > 0) {
                  return <span className="text-orange-600">{t('createDocument.invoices.unallocatedAmount')}: ₪{diff.toFixed(2)}</span>;
                }
                return <span className="text-red-600 font-semibold">{t('createDocument.invoices.paymentShortfall')}: ₪{Math.abs(diff).toFixed(2)}</span>;
              })()}
            </div>
            {(() => {
              const paymentTotal = data.payments.reduce((s, p) => s + p.amount, 0);
              const allocationTotal = data.invoiceAllocations.reduce((s, a) => s + a.amount_close, 0);
              if (paymentTotal < allocationTotal - 0.01) {
                return (
                  <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    ⚠️ {t('createDocument.review.paymentShortfallWarning', {
                      paid: paymentTotal.toFixed(2),
                      required: allocationTotal.toFixed(2),
                    })}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}

        {/* Totals */}
        <div className="space-y-2 border-t border-gray-100 pt-4">
          <div className="flex justify-between text-sm text-gray-600">
            <span>{t('createDocument.review.subtotal')}</span>
            <span>₪{Number(subtotal).toFixed(2)}</span>
          </div>
          {showVat && vatRate != null && (
            <div className="flex justify-between text-sm text-gray-500">
              <span>
                {t('createDocument.review.vat')} ({vatRate}%)
              </span>
              <span>
                {data.priceIncludeVat
                  ? t('createDocument.review.vat')
                  : `₪${Number(vatAmount).toFixed(2)}`}
              </span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold text-gray-900">
            <span>{t('createDocument.review.total')}</span>
            <span>₪{Number(total).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Comments */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {t('createDocument.review.comments')}
        </label>
        <textarea
          value={data.comments}
          onChange={(e) => onCommentsChange(e.target.value)}
          placeholder={t('createDocument.review.commentsPlaceholder')}
          rows={3}
          className="w-full rounded-xl border border-gray-300 p-3 text-sm text-gray-800 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
      </div>

      {/* Error Banner */}
      {error && (
        <div className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm font-medium text-red-700">{error}</p>
          <button
            type="button"
            onClick={handleCreate}
            className="rounded-lg bg-red-100 px-3 py-1 text-sm font-medium text-red-700 hover:bg-red-200 transition-colors"
          >
            {t('createDocument.review.tryAgain')}
          </button>
        </div>
      )}

      {/* Allocation mismatch error */}
      {allocationMismatch && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <span className="text-lg">⚠️</span>
          <p className="text-sm font-medium text-red-700">
            {t('createDocument.review.allocationMismatchError', {
              allocated: data.invoiceAllocations.reduce((s, a) => s + a.amount_close, 0).toFixed(2),
              paid: data.payments.reduce((s, p) => s + p.amount, 0).toFixed(2),
            })}
          </p>
        </div>
      )}

      {/* Create Button */}
      <button
        type="button"
        onClick={handleCreate}
        disabled={loading || allocationMismatch}
        className={[
          'w-full rounded-xl py-4 text-lg font-semibold transition-colors',
          loading || allocationMismatch
            ? 'cursor-not-allowed bg-gray-300 text-gray-500'
            : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800',
        ].join(' ')}
      >
        {loading ? (
          <span className="inline-flex items-center gap-2">
            <svg
              className="h-5 w-5 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            {t('createDocument.review.creating')}
          </span>
        ) : (
          t('createDocument.review.create')
        )}
      </button>
    </div>
  );
}
